import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { migApi, errMsg } from "../lib";

const IGNORE = "";

export function MapStep({ cid, batch, types, onMapped }) {
  const spec = types?.record_types?.find((t) => t.id === batch.record_type);
  const fields = spec?.fields || [];
  const initial = Object.fromEntries((batch.headers || []).map((h) => [h, batch.mapping?.[h] || batch.suggested_mapping?.[h]?.field || IGNORE]));
  const [map, setMap] = useState(initial);
  const [opts, setOpts] = useState({ create_missing_customers: batch.options?.create_missing_customers ?? true, record_historical_payments: batch.options?.record_historical_payments ?? false });
  const [busy, setBusy] = useState(false);
  const used = Object.values(map).filter(Boolean);
  const dupes = used.filter((v, i) => used.indexOf(v) !== i);
  const missing = fields.filter((f) => f.required && !used.includes(f.key));
  const save = async () => {
    if (missing.length) { toast.error(`Map required fields: ${missing.map((m) => m.label).join(", ")}`); return; }
    if (dupes.length) { toast.error("Each NEEC field can only be mapped once"); return; }
    setBusy(true);
    try { onMapped(await migApi.mapping(cid, batch.id, { mapping: Object.fromEntries(Object.entries(map).filter(([, v]) => v)), options: opts })); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const isHist = batch.record_type === "historical_jobs";
  return (
    <div className="space-y-3" data-testid="mig-step-map">
      <p className="text-xs text-muted-foreground">{batch.row_count} rows · {batch.headers?.length} columns. Suggestions with low confidence are flagged; set a column to Ignore to skip it.</p>
      <div className="space-y-1">
        {(batch.headers || []).map((h) => {
          const sug = batch.suggested_mapping?.[h]; const low = sug && sug.confidence < 0.8 && map[h] === sug.field; const sample = batch.sample_rows?.[0]?.[h];
          return (
            <div key={h} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-md border border-border px-2 py-1.5" data-testid={`mig-map-row-${h}`}>
              <div className="min-w-0"><p className="text-xs font-semibold truncate">{h}</p>{sample && <p className="text-[10px] text-muted-foreground truncate">e.g. {String(sample)}</p>}</div>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
              <div>
                <select value={map[h]} onChange={(e) => setMap({ ...map, [h]: e.target.value })} className={`h-8 w-full rounded-md border bg-background px-2 text-xs ${dupes.includes(map[h]) ? "border-rose-500" : "border-input"}`} data-testid={`mig-map-select-${h}`}>
                  <option value={IGNORE}>Ignore</option>{fields.map((f) => <option key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</option>)}
                </select>
                {low && <p className="text-[10px] text-amber-700">Low-confidence suggestion, please confirm</p>}
              </div>
            </div>
          );
        })}
      </div>
      {missing.length > 0 && <p className="text-xs text-rose-600" data-testid="mig-map-missing">Required: {missing.map((m) => m.label).join(", ")}</p>}
      {(isHist || ["jobs", "quotes", "invoices", "payments"].includes(batch.record_type)) && (
        <div className="rounded-lg border border-border p-2 space-y-1" data-testid="mig-map-options">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={opts.create_missing_customers} onChange={(e) => setOpts({ ...opts, create_missing_customers: e.target.checked })} data-testid="mig-opt-create-customers" /> Create customers that don't exist yet</label>
          {isHist && <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={opts.record_historical_payments} onChange={(e) => setOpts({ ...opts, record_historical_payments: e.target.checked })} data-testid="mig-opt-historical-payments" /> Also record collected amounts as historical payments (counts toward reports)</label>}
        </div>
      )}
      <Button size="sm" disabled={busy} onClick={save} data-testid="mig-map-save">{busy ? "Saving..." : "Save mapping & continue"}</Button>
    </div>
  );
}
